import { ColumnDef, Row } from "@tanstack/react-table";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Table } from "@jhool-io/fe-components";
import { useFetchNoteActivities } from "../../../../hooks/queries/note";
import {
    displayNameInRightFormat,
    formatDate,
} from "../../../../utils/helpers";
import ListState from "../../../../components/ListState/ListState";
import MobileListItem from "../../../../shared-ui/MobileListItem/MobileListItem";
import Loader from "../../../../shared-ui/Loader/Loader";
import {
    IActivity,
    SupervisorChainActivityHop,
    SupervisorChainHopReason,
} from "../../types/notes.types";

const reasonLabelMap: Record<SupervisorChainHopReason, string> = {
    default_supervisor: "default supervisor",
    category_override: "category supervisor",
    insurance_override: "insurance supervisor",
};

function buildChainDescription(chain: SupervisorChainActivityHop[]) {
    if (chain.length < 2) return null;
    const segments = chain.map((hop, i) => {
        const contextName = i === 0 ? "the note author" : chain[i - 1].name;
        return `${reasonLabelMap[hop.reason]} of ${contextName}`;
    });
    const terminal = chain[chain.length - 1];
    const terminalSegment = segments[segments.length - 1];
    const priorSegments = segments.slice(0, -1).reverse();
    if (priorSegments.length === 0) {
        return `Routed to ${terminal.name} — ${terminalSegment}`;
    }
    return (
        `Routed to ${terminal.name} — ${terminalSegment}` +
        ` (who is the ${priorSegments.join(", who is the ")})`
    );
}

const columns: ColumnDef<IActivity>[] = [
    {
        accessorKey: "activity_datetime",
        header: "date",
        cell: ({ row }) => formatDate(row.original.activity_datetime),
    },
    {
        accessorKey: "note_activity_id",
        header: "Time",
        cell: ({ row }) =>
            formatDate(row.original.activity_datetime, false, "h:mma"),
    },
    {
        accessorKey: "activity_user",
        header: "AUTHOR",
        cell: ({ row }) => (
            <span className="capitalize">
                {displayNameInRightFormat({
                    firstName: row.original.activity_user.first_name,
                    lastName: row.original.activity_user.last_name,
                })}
            </span>
        ),
    },
    {
        accessorKey: "activity_description",
        header: "DESCRIPTION",
        cell: ({ row }) => {
            const { metadata } = row.original;
            const description = row.original.activity_description;
            if (metadata?.chain && metadata.chain.length > 1) {
                const chainText = buildChainDescription(metadata.chain);
                if (chainText) return chainText;
            }
            if (metadata?.flag_for_audit_notes) {
                return `${description} - ${metadata.flag_for_audit_notes}`;
            }
            if (metadata?.supervisor_name) {
                return `${description} - ${metadata.supervisor_name}`;
            }
            return description;
        },
    },
];

export default function NoteActivityList() {
    // Get id from url params
    const params = useParams();
    const noteId = params.noteId as string;

    const navigate = useNavigate();
    const location = useLocation();

    const { data, error, isLoading, isSuccess } =
        useFetchNoteActivities(noteId);

    const handleRowClick = (row: Row<IActivity>) => {
        if (row.original.metadata && row.original.metadata.addendum_field_id) {
            navigate(
                `${location.pathname}#${row.original.metadata.addendum_field_id}?note_tab=content`
            );
        }
    };

    return (
        <>
            {isLoading && <Loader />}
            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display notes activity at this time please try again later`
                    }
                />
            )}
            {data && isSuccess && (
                <>
                    <div className="block" style={{ borderBottom: "none" }}>
                        <Table
                            columns={columns}
                            data={data.data.note_activities}
                            handleRowClick={(row) => {
                                return row.original.metadata &&
                                    row.original.metadata.addendum_field_id
                                    ? handleRowClick(row)
                                    : undefined;
                            }}
                            handleRowStyle={(row) =>
                                !row.original.metadata
                                    ? {
                                          pointerEvents: "none",
                                      }
                                    : {
                                          pointerEvents: "all",
                                      }
                            }
                        />
                    </div>
                    <div className="hidden" role="table">
                        <div className="flex flex-col gap-y-12">
                            {data.data.note_activities.map((activity) => (
                                <MobileListItem
                                    key={activity.note_activity_id}
                                    showCheckButton={false}
                                    topChildren={
                                        <div className="flex flex-col gap-y-4">
                                            <span className="capitalize font-semibold text-base">
                                                {`${activity.activity_user.first_name} ${activity.activity_user.last_name}`}
                                            </span>
                                        </div>
                                    }
                                    bottomChildren={
                                        <div className="flex flex-col gap-y-8 w-full items-start">
                                            <div className="flex items-center w-full text-left gap-x-4">
                                                <div className="basis-1/2">
                                                    <span className="block font-medium text-xss uppercase">
                                                        date
                                                    </span>
                                                    <span className="block text-gray font-semibold text-xs">
                                                        {formatDate(
                                                            activity.activity_datetime
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="basis-1/2">
                                                    <span className="block font-medium text-xss uppercase">
                                                        Time
                                                    </span>
                                                    <span className="block text-gray font-semibold text-xs">
                                                        {formatDate(
                                                            activity.activity_datetime,
                                                            false,
                                                            "h:mma"
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="w-full">
                                                <span className="block font-medium text-xss uppercase mb-4">
                                                    description
                                                </span>
                                                <div className="block !text-gray !font-semibold !text-xs">
                                                    {}
                                                </div>
                                            </div>
                                        </div>
                                    }
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
